import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../common/Button";
import Input from "../common/Input";
import { proposalService } from "../../services/proposals";
import { clientService } from "../../services/clients";
import { Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";

const ProposalBuilder = ({ proposal, onSuccess }) => {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset } = useForm({
    defaultValues: proposal
      ? {
          title: proposal.title,
          clientId: proposal.client?._id || proposal.client,
          description: proposal.description || "",
          scope: proposal.scope || "",
          timeline: proposal.timeline || "",
          validityDays: proposal.validityDays || 30,
          terms: proposal.terms || "",
        }
      : { validityDays: 30 },
  });
  const [services, setServices] = useState(proposal?.services || []);
  const [loading, setLoading] = useState(false);

  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: clientService.getAll,
  });

  useEffect(() => {
    if (proposal) {
      reset({
        title: proposal.title,
        clientId: proposal.client?._id || proposal.client,
        description: proposal.description || "",
        scope: proposal.scope || "",
        timeline: proposal.timeline || "",
        validityDays: proposal.validityDays || 30,
        terms: proposal.terms || "",
      });
      setServices(proposal.services || []);
    } else {
      reset({ validityDays: 30 });
      setServices([]);
    }
  }, [proposal, reset]);

  const addService = () => {
    setServices([...services, { name: "", description: "", price: 0 }]);
  };

  const removeService = (index) => {
    setServices(services.filter((_, i) => i !== index));
  };

  const updateService = (index, field, value) => {
    const updated = [...services];
    updated[index][field] = value;
    setServices(updated);
  };

  const totalAmount = services.reduce((sum, s) => sum + parseFloat(s.price || 0), 0);

  const onSubmit = async (data) => {
    if (services.length === 0) {
      toast.error("Add at least one service");
      return;
    }
    const proposalData = {
      title: data.title,
      client: data.clientId,
      description: data.description,
      scope: data.scope,
      timeline: data.timeline,
      validityDays: parseInt(data.validityDays) || 30,
      terms: data.terms,
      services,
      totalAmount,
    };
    setLoading(true);
    try {
      if (proposal) {
        await proposalService.update(proposal._id, proposalData);
        toast.success("Proposal updated");
      } else {
        await proposalService.create(proposalData);
        toast.success("Proposal created");
      }
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      reset();
      setServices([]);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving proposal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <Input label="Proposal Title" {...register("title", { required: true })} placeholder="e.g. Digital Marketing Proposal for XYZ" />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
        <select
          {...register("clientId", { required: true })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
        >
          <option value="">Select Client</option>
          {clients?.map((client) => (
            <option key={client._id} value={client._id}>
              {client.name} {client.company ? `(${client.company})` : ""}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Project Description</label>
        <textarea
          {...register("description")}
          rows={3}
          placeholder="Brief overview of what this proposal covers..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Scope of Work</label>
        <textarea
          {...register("scope")}
          rows={3}
          placeholder="Define what's included in this proposal..."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input label="Timeline" {...register("timeline")} placeholder="e.g. 3 months" />
        <Input label="Validity (days)" type="number" {...register("validityDays")} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">Services & Pricing</h3>
          <button
            type="button"
            onClick={addService}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <Plus className="w-4 h-4" /> Add Service
          </button>
        </div>
        {services.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-lg">No services added yet. Click "Add Service" above.</p>
        )}
        <div className="space-y-3">
          {services.map((s, idx) => (
            <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-2">
                  <input
                    placeholder="Service name (e.g. Social Media Management)"
                    value={s.name}
                    onChange={(e) => updateService(idx, "name", e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  <input
                    placeholder="Brief description of this service (optional)"
                    value={s.description || ""}
                    onChange={(e) => updateService(idx, "description", e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
                <input
                  type="number"
                  placeholder="Price"
                  value={s.price}
                  onChange={(e) => updateService(idx, "price", e.target.value)}
                  className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => removeService(idx)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        {services.length > 0 && (
          <div className="flex justify-end mt-3 pr-10">
            <div className="text-right">
              <span className="text-sm text-gray-500">Total: </span>
              <span className="text-lg font-bold text-gray-900">₹{totalAmount.toLocaleString("en-IN")}</span>
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
        <textarea
          {...register("terms")}
          rows={3}
          placeholder="Payment terms, cancellation policy, etc."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
        />
      </div>

      <div className="pt-2">
        <Button type="submit" disabled={loading} className="w-full">
          {loading ? "Saving..." : proposal ? "Update Proposal" : "Create Proposal"}
        </Button>
      </div>
    </form>
  );
};

export default ProposalBuilder;
